import { SearchOutlined } from '@ant-design/icons';
import { Input, Tag } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import colorTheme from '../../../../constant/colors';
import DownIcon from '../../../../components/Icon/icons/DownIcon';
import { Checkbox } from '../../../../components/Checkbox';
import useGetAllStore from '../../../package-sms/services/hooks/useGetAllStore';
import { formatPhone } from '../../../../utils/formatPhone';

interface SalonItem {
  id: string;
  name: string;
  phone: string;
}

interface SalonSearchSelectProps {
  refetch: (param?: { shopId?: string }) => void;
}

const SalonSearchSelect = ({ refetch }: SalonSearchSelectProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { data } = useGetAllStore();
  const [selectedSalons, setSelectedSalons] = useState<SalonItem | null>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredSalons = useMemo(() => {
    return data
      ?.map((salon: any) => ({
        id: salon.shopId,
        name: salon.shopName,
        phone: salon.phoneShop,
      }))
      .filter((salon: SalonItem) => salon.name.toLowerCase().includes(search.toLowerCase()));
  }, [search, data]);

  const toggleSalon = (salon: SalonItem) => {
    if (selectedSalons?.id === salon.id) {
      setSelectedSalons(null);
    } else {
      setSelectedSalons(salon);
    }
  };

  const removeSalon = (salon: SalonItem) => {
    setSelectedSalons((prev) => (prev?.id === salon.id ? null : prev));
  };

  useEffect(() => {
    refetch({
      shopId: selectedSalons?.id || undefined,
    });
  }, [selectedSalons]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Wrapper ref={wrapperRef}>
      <SearchBox onClick={() => setOpen((prev) => !prev)} className={open ? 'open' : ''}>
        <SelectedTags>
          {selectedSalons ? (
            <StyledTag
              key={selectedSalons.id}
              closable
              onClose={(e) => {
                e.preventDefault();
                e.stopPropagation();

                removeSalon(selectedSalons);
              }}
            >
              {selectedSalons.name}
            </StyledTag>
          ) : (
            <Placeholder>Select salon</Placeholder>
          )}
        </SelectedTags>

        <ArrowIcon $open={open}>
          <DownIcon />
        </ArrowIcon>
      </SearchBox>

      {open && (
        <Dropdown>
          <SearchInputWrapper>
            <SearchInput
              placeholder='Search salon...'
              prefix={<SearchOutlined />}
              value={search}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setSearch(e.target.value)}
            />
          </SearchInputWrapper>

          <SalonList>
            {filteredSalons.map((salon) => {
              const checked = selectedSalons?.id === salon.id;

              return (
                <SalonItemRow key={salon.id} $active={checked} onClick={() => toggleSalon(salon)}>
                  <Left>
                    <Checkbox value={checked} />

                    <SalonName>{salon.name}</SalonName>
                  </Left>

                  <Phone>{formatPhone(salon.phone)}</Phone>
                </SalonItemRow>
              );
            })}
          </SalonList>
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default SalonSearchSelect;
const Wrapper = styled.div`
  width: 100%;
  max-width: 600px;
  position: relative;
  font-family: 'Poppins';
`;

const SearchBox = styled.div`
  height: 48px;

  background: #ffffff;

  border: 1px solid #dadada;
  border-radius: 8px;

  padding: 8px 12px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  cursor: pointer;

  &.open {
    border-color: ${colorTheme.blue};
  }
  &:hover {
    border-color: ${colorTheme.blue};
  }
`;

const SelectedTags = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  flex: 1;
`;

const StyledTag = styled(Tag)`
  margin-inline-end: 0;
  padding: 1px 8px;
  font-size: 14px;
  border: 1px solid #f2f3f5;
  background: #f6f7fc;
`;

const Placeholder = styled.div`
  color: #9ca3af;
  font-size: 14px;
`;

const ArrowIcon = styled.div<{
  $open: boolean;
}>`
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;

  width: 100%;
  z-index: 1000;

  background: #ffffff;

  border-radius: 8px;

  overflow: hidden;

  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
`;

const SearchInputWrapper = styled.div`
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
`;

const SearchInput = styled(Input)`
  height: 40px;
`;

const SalonList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 999px;
  }
`;

const SalonItemRow = styled.div<{
  $active: boolean;
}>`
  padding: 4px 24px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  cursor: pointer;

  transition: all 0.2s ease;

  background: ${({ $active }) => ($active ? '#F6F7FC' : '#ffffff')};

  &:hover {
    background: #f5f7fa;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SalonName = styled.div`
  font-size: 14px;
  color: #1d2129;
  font-weight: 500;
`;

const Phone = styled.div`
  font-size: 14px;
  color: #1d2129;
  font-weight: 400;
`;
